import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

const api = {
  // Stats
  async getStats() {
    const response = await client.get('/stats');
    return response.data;
  },

  // Datasets
  async getDatasets() {
    const response = await client.get('/datasets');
    return response.data;
  },

  async getDataset(id) {
    const response = await client.get(`/datasets/${id}`);
    return response.data;
  },

  async deleteDataset(id) {
    const response = await client.delete(`/datasets/${id}`);
    return response.data;
  },

  async uploadDataset(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await client.post('/datasets/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Conversations
  async getConversations(skip = 0, limit = 50, platform = undefined) {
    const params = { skip, limit };
    if (platform) params.platform = platform;
    
    const response = await client.get('/conversations', { params });
    return response.data;
  },

  async getConversation(id) {
    const response = await client.get(`/conversations/${id}`);
    return response.data;
  },

  async getDatasetConversations(datasetId, skip = 0, limit = 50) {
    const response = await client.get(`/datasets/${datasetId}/conversations`, {
      params: { skip, limit }
    });
    return response.data;
  },

  // Duplicates
  async findDuplicates() {
    const response = await client.post('/duplicates/find');
    return response.data;
  },

  async removeDuplicates(keepFirst = true) {
    const response = await client.post('/duplicates/remove', null, {
      params: { keep_first: keepFirst }
    });
    return response.data;
  },

  // Export
  async exportDatasets(datasetIds, format, includeSystem = false) {
    const response = await client.post('/export', {
      dataset_ids: datasetIds,
      format,
      include_system: includeSystem
    }, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Ollama
  async getOllamaStatus() {
    const response = await client.get('/ollama/status');
    return response.data;
  },

  async updateOllamaConfig(config) {
    const response = await client.post('/ollama/config', config);
    return response.data;
  },

  async categorizeConversation(conversationId) {
    const response = await client.post('/ollama/categorize', null, {
      params: { conversation_id: conversationId }
    });
    return response.data;
  },

  // Quality Scoring
  async scoreConversation(conversationId) {
    const response = await client.post('/ollama/quality-score', null, {
      params: { conversation_id: conversationId }
    });
    return response.data;
  },

  async batchScore(datasetId, limit = 10) {
    const params = { limit };
    if (datasetId) params.dataset_id = datasetId;
    
    const response = await client.post('/ollama/batch-score', null, { params });
    return response.data;
  },

  async getScoredConversations(minScore = 0, recommendation = undefined, skip = 0, limit = 50) {
    const params = { min_score: minScore, skip, limit };
    if (recommendation) params.recommendation = recommendation;
    
    const response = await client.get('/conversations/scored', { params });
    return response.data;
  },

  // Tags
  async addTags(conversationId, tags) {
    const response = await client.post(`/conversations/${conversationId}/tags`, { tags });
    return response.data;
  },

  async removeTags(conversationId, tags) {
    const response = await client.delete(`/conversations/${conversationId}/tags`, { data: { tags } });
    return response.data;
  },

  async getAllTags() {
    const response = await client.get('/tags');
    return response.data;
  },

  // Smart Filtering
  async filterConversations(filters) {
    const response = await client.post('/conversations/filter', null, { params: filters });
    return response.data;
  },

  // Detailed Stats
  async getDetailedStats() {
    const response = await client.get('/stats/detailed');
    return response.data;
  },

  // Training Runs
  async getTrainingRuns() {
    const response = await client.get('/training/runs');
    return response.data;
  },

  async getTrainingRun(id) {
    const response = await client.get(`/training/runs/${id}`);
    return response.data;
  },

  async createTrainingRun(data) {
    const response = await client.post('/training/runs', data);
    return response.data;
  },

  async deleteTrainingRun(id) {
    const response = await client.delete(`/training/runs/${id}`);
    return response.data;
  },

  async prepareTrainingData(runId) {
    const response = await client.post(`/training/runs/${runId}/prepare`);
    return response.data;
  },

  async startTraining(runId) {
    const response = await client.post(`/training/runs/${runId}/start`);
    return response.data;
  },

  async downloadTrainingFile(runId) {
    const response = await client.get(`/training/runs/${runId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  async getTrainingStats() {
    const response = await client.get('/training/stats');
    return response.data;
  },

  // Conversation Splitting
  async splitConversation(conversationId, config = {}) {
    const response = await client.post(`/conversations/${conversationId}/split`, config);
    return response.data;
  },

  async batchSplitConversations(datasetId, minMessages = 15, config = {}) {
    const params = { min_messages: minMessages };
    if (datasetId) params.dataset_id = datasetId;
    
    const response = await client.post('/conversations/batch-split', config, { params });
    return response.data;
  },
};

export default api;
